import { useRef, useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import type { QRConfig, QRGradient, QRLogo } from '@/types/qrCode';
import { isAllowedLogoMimeType } from '@/components/qr/qrPresets';
import { QRColorDotControls } from '@/components/qr/QRColorDotControls';
import { QRLogoControlsSection } from '@/components/qr/QRLogoControlsSection';
import { QRExportActions } from '@/components/qr/QRExportActions';

// qr-code-styling uses browser APIs
import QRCodeStyling from 'qr-code-styling';

// -- Props ---------------------------------------------------------

interface QRCodeDesignerProps {
  value: string;
  config?: QRConfig;
  onConfigChange?: (config: QRConfig) => void;
  size?: number;
  showDesignControls?: boolean;
  showExport?: boolean;
  showCopyUrl?: boolean;
  showLogoControls?: boolean;
  onSaveImage?: (blob: Blob) => void;
  isSaving?: boolean;
  logos?: QRLogo[];
  onUploadLogo?: (file: File, name: string) => void;
  isUploadingLogo?: boolean;
  onDeleteLogo?: (id: string) => void;
}

// -- Ref handle ----------------------------------------------------

export interface QRCodeDesignerHandle {
  saveImage: () => Promise<void>;
}

// -- Component -----------------------------------------------------

export const QRCodeDesigner = forwardRef<QRCodeDesignerHandle, QRCodeDesignerProps>(function QRCodeDesigner({
  value,
  config = {},
  onConfigChange,
  size = 200,
  showDesignControls = true,
  showExport = true,
  showCopyUrl = false,
  showLogoControls = false,
  onSaveImage,
  isSaving,
  logos = [],
  onUploadLogo,
  isUploadingLogo,
  onDeleteLogo,
}, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrInstance = useRef<QRCodeStyling | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [showFgGradient, setShowFgGradient] = useState(!!config.fgGradient);
  const [showBgGradient, setShowBgGradient] = useState(!!config.bgGradient);

  const {
    fgColor = '#FFFFFF',
    bgColor = '#111111',
    fgGradient,
    bgGradient,
    dotStyle = 'square',
    cornerSquareStyle,
    cornerDotStyle,
    logoEnabled = true,
    logoUrl,
    logoSize = 0.25,
    logoMargin = 4,
  } = config;

  // Resolve logo URL
  const resolvedLogoUrl = logoEnabled
    ? (logoUrl || '/ht-logo.png')
    : undefined;

  // Convert QRGradient to qr-code-styling gradient format
  const gradientToLib = useCallback((g: QRGradient) => ({
    type: g.type,
    rotation: g.rotation ? (g.rotation * Math.PI) / 180 : 0,
    colorStops: g.colorStops,
  }), []);

  // Build qr-code-styling options
  const qrOptions = useMemo(() => {
    return {
      width: size,
      height: size,
      data: value || 'https://hollywoodtrucks.com',
      image: resolvedLogoUrl,
      dotsOptions: {
        type: dotStyle || 'square',
        color: fgGradient ? undefined : fgColor,
        gradient: fgGradient ? gradientToLib(fgGradient) : undefined,
      },
      backgroundOptions: {
        color: bgGradient ? undefined : bgColor,
        gradient: bgGradient ? gradientToLib(bgGradient) : undefined,
      },
      cornersSquareOptions: cornerSquareStyle ? { type: cornerSquareStyle } : {},
      cornersDotOptions: cornerDotStyle ? { type: cornerDotStyle } : {},
      imageOptions: resolvedLogoUrl
        ? { imageSize: logoSize, margin: logoMargin, hideBackgroundDots: true, crossOrigin: 'anonymous' }
        : { hideBackgroundDots: false },
      qrOptions: { errorCorrectionLevel: resolvedLogoUrl ? ('H' as const) : ('M' as const) },
    };
  }, [value, size, fgColor, bgColor, fgGradient, bgGradient, dotStyle, cornerSquareStyle, cornerDotStyle, resolvedLogoUrl, logoSize, logoMargin, gradientToLib]);

  // Track previous logo state to detect toggle changes
  const prevLogoRef = useRef(resolvedLogoUrl);

  // Create or recreate QR instance
  const createQRInstance = useCallback((options: typeof qrOptions) => {
    if (!QRCodeStyling || !containerRef.current) return;
    try {
      // Safe cleanup — remove all children without innerHTML
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
      qrInstance.current = new QRCodeStyling(options);
      qrInstance.current.append(containerRef.current);
    } catch (err) {
      console.warn('QR code render failed:', err);
    }
  }, []);

  // Mount QR instance once
  useEffect(() => {
    createQRInstance(qrOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update QR on options change — recreate instance when logo is toggled
  // because qr-code-styling doesn't handle image add/remove reliably on mobile
  useEffect(() => {
    if (!qrInstance.current) return;
    const logoToggled =
      (prevLogoRef.current && !resolvedLogoUrl) ||
      (!prevLogoRef.current && resolvedLogoUrl);
    prevLogoRef.current = resolvedLogoUrl;

    if (logoToggled) {
      // Full recreate when logo is added or removed
      createQRInstance(qrOptions);
    } else {
      try {
        qrInstance.current.update(qrOptions);
      } catch (err) {
        // If update fails, try full recreate as fallback
        console.warn('QR update failed, recreating:', err);
        createQRInstance(qrOptions);
      }
    }
  }, [qrOptions, resolvedLogoUrl, createQRInstance]);

  // -- Config helpers --

  // Use a ref to always read the latest config, avoiding stale closures
  // when multiple changes happen in rapid succession
  const configRef = useRef(config);
  configRef.current = config;

  const updateConfig = useCallback((patch: Partial<QRConfig>) => {
    onConfigChange?.({ ...configRef.current, ...patch });
  }, [onConfigChange]);

  // -- Export handlers --

  const handleDownloadPNG = useCallback(async () => {
    qrInstance.current?.download({ name: 'qr-code', extension: 'png' });
  }, []);

  const handleDownloadSVG = useCallback(async () => {
    qrInstance.current?.download({ name: 'qr-code', extension: 'svg' });
  }, []);

  const handleSaveToLibrary = useCallback(async () => {
    if (!qrInstance.current || !onSaveImage) return;
    try {
      const blob = await qrInstance.current.getRawData('png');
      if (blob) onSaveImage(blob as Blob);
    } catch (err) {
      console.warn('Failed to get QR image data:', err);
    }
  }, [onSaveImage]);

  // Expose saveImage method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      saveImage: () => handleSaveToLibrary(),
    }),
    [handleSaveToLibrary]
  );

  const handleCopyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  // -- Logo handlers --

  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAllowedLogoMimeType(file.type)) {
      toast.error('Logo must be PNG, JPEG, SVG, or WebP.');
      e.target.value = '';
      return;
    }

    if (onUploadLogo) {
      // Upload to Supabase Storage via API
      const name = file.name.replace(/\.[^/.]+$/, '');
      onUploadLogo(file, name);
    } else {
      // Fallback: client-side data URL
      const reader = new FileReader();
      reader.onload = () => {
        updateConfig({ logoUrl: reader.result as string, logoEnabled: true });
      };
      reader.readAsDataURL(file);
    }
  }, [onUploadLogo, updateConfig]);

  // -- Gradient helpers --

  const defaultGradient = useCallback((): QRGradient => ({
    type: 'linear',
    rotation: 0,
    colorStops: [
      { offset: 0, color: '#8FB560' },
      { offset: 1, color: '#FFFFFF' },
    ],
  }), []);

  return (
    <div className="flex flex-col items-center gap-5 w-full">
      {/* QR Code render target */}
      <div
        ref={containerRef}
        className="bg-[#111] p-5 rounded-xl border border-[var(--card-border)]"
      />

      {showDesignControls && (
        <>
          <QRColorDotControls
            fgColor={fgColor}
            bgColor={bgColor}
            fgGradient={fgGradient}
            bgGradient={bgGradient}
            dotStyle={dotStyle}
            showFgGradient={showFgGradient}
            showBgGradient={showBgGradient}
            setShowFgGradient={setShowFgGradient}
            setShowBgGradient={setShowBgGradient}
            updateConfig={updateConfig}
            defaultGradient={defaultGradient}
          />

      {showLogoControls && (
        <QRLogoControlsSection
          logoEnabled={logoEnabled}
          logoUrl={logoUrl}
          logoSize={logoSize}
          logoMargin={logoMargin}
          logos={logos}
          isUploadingLogo={isUploadingLogo}
          logoInputRef={logoInputRef}
          onToggleLogoEnabled={() => updateConfig({ logoEnabled: !logoEnabled })}
          onSelectDefaultLogo={() => updateConfig({ logoUrl: undefined })}
          onSelectLogoUrl={(url) => updateConfig({ logoUrl: url })}
          onDeleteLogo={onDeleteLogo}
          onLogoFileChange={handleLogoUpload}
          onLogoSizeChange={(v) => updateConfig({ logoSize: v })}
          onLogoMarginChange={(v) => updateConfig({ logoMargin: v })}
        />
      )}
        </>
      )}

      {/* Copy URL */}
      {showCopyUrl && (
        <button
          onClick={handleCopyUrl}
          className="flex items-center gap-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-main)] transition-colors"
        >
          {copied ? <Check size={12} className="text-brand" /> : <Copy size={12} />}
          {copied ? 'Copied!' : value}
        </button>
      )}

      {showExport && (
        <QRExportActions
          onDownloadPNG={handleDownloadPNG}
          onDownloadSVG={handleDownloadSVG}
          onSaveToLibrary={handleSaveToLibrary}
          showSaveToLibrary={!!onSaveImage}
          isSaving={isSaving}
        />
      )}
    </div>
  );
});
